import React, { useState, useEffect, useCallback } from 'react';
import { 
  BookOpen, Download, RefreshCw, FileText, Users,
  Calendar, Filter, ArrowUpDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { formatDateIT } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegistroSoci() {
  const [soci, setSoci] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroAnno, setFiltroAnno] = useState('tutti');
  const [exporting, setExporting] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [ordinamentoPDF, setOrdinamentoPDF] = useState('data');

  const fetchSoci = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/soci?stato=tutti&limit=200`;
      if (filtroAnno !== 'tutti') {
        url += `&anno_iscrizione=${filtroAnno}`;
      }
      
      const response = await fetch(url, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        // Sort by numero_tessera
        const sorted = (data.soci || []).sort((a, b) => {
          const numA = parseInt(a.numero_tessera) || 0;
          const numB = parseInt(b.numero_tessera) || 0;
          return numA - numB;
        });
        setSoci(sorted);
      } else {
        console.error('Errore API:', response.status);
      }
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [filtroAnno]);

  useEffect(() => {
    fetchSoci();
  }, [fetchSoci]);

  const handleExportCSV = () => {
    if (soci.length === 0) {
      toast.error('Nessun dato da esportare');
      return;
    }

    // Create CSV
    const headers = ['N. Tessera', 'Cognome', 'Nome', 'Codice Fiscale', 'Data Nascita', 'Email', 'Cellulare', 'Città', 'Tipo Socio', 'Data Iscrizione', 'Stato'];
    const rows = soci.map(s => [
      s.numero_tessera || '',
      s.cognome || '',
      s.nome || '',
      s.codice_fiscale || '',
      s.data_nascita || '',
      s.email || '',
      s.cellulare || '',
      s.citta || '',
      s.tipo_socio || '',
      s.data_iscrizione || '',
      s.stato_socio || ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `registro_soci_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    toast.success('Registro esportato in CSV');
  };

  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reports/registro-soci-pdf?ordine=${ordinamentoPDF}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `registro_soci_${ordinamentoPDF}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        const ordineLabel = {alfabetico: 'Alfabetico', progressivo: 'N° Tessera', data: 'Data'}[ordinamentoPDF];
        toast.success(`Registro Soci PDF scaricato (${ordineLabel})`);
      } else {
        toast.error('Errore nel generare il PDF');
      }
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore di connessione');
    } finally {
      setExportingPDF(false);
    }
  };

  const getStatoBadge = (stato) => {
    const colors = {
      attivo: 'bg-green-100 text-green-800',
      sospeso: 'bg-yellow-100 text-yellow-800',
      cessato: 'bg-red-100 text-red-800'
    };
    return colors[stato] || 'bg-gray-100 text-gray-800';
  };

  const anni = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= currentYear - 10; i--) {
    anni.push(i);
  }

  // Stats
  const totaleAttivi = soci.filter(s => s.stato_socio === 'attivo').length;
  const totaleSospesi = soci.filter(s => s.stato_socio === 'sospeso').length;
  const totaleCessati = soci.filter(s => s.stato_socio === 'cessato').length;

  return (
    <div className="space-y-6" data-testid="registro-soci-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="h-6 w-6" />
            Registro Soci
          </h1>
          <p className="text-gray-500 mt-1">Libro dei soci dell'associazione</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-4 h-4 text-slate-500" />
            <Select value={ordinamentoPDF} onValueChange={setOrdinamentoPDF}>
              <SelectTrigger className="w-[160px]" data-testid="select-ordinamento-soci">
                <SelectValue placeholder="Ordinamento PDF" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alfabetico">Alfabetico (A-Z)</SelectItem>
                <SelectItem value="progressivo">N° Tessera</SelectItem>
                <SelectItem value="data">Data Iscrizione</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" />
            Esporta CSV
          </Button>
          <Button 
            variant="default" 
            onClick={handleExportPDF} 
            disabled={exportingPDF}
            data-testid="export-pdf-btn"
            className="bg-red-600 hover:bg-red-700"
          >
            {exportingPDF ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 mr-2" />
            )}
            Stampa PDF
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{soci.length}</div>
            <p className="text-sm text-gray-500">Soci Totali</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-green-600">{totaleAttivi}</div>
            <p className="text-sm text-gray-500">Attivi</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-yellow-600">{totaleSospesi}</div>
            <p className="text-sm text-gray-500">Sospesi</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-red-600">{totaleCessati}</div>
            <p className="text-sm text-gray-500">Cessati</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-500">Filtra per anno iscrizione:</span>
            </div>
            <Select value={filtroAnno} onValueChange={setFiltroAnno}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Anno" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti gli anni</SelectItem>
                {anni.map(a => (
                  <SelectItem key={a} value={a.toString()}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={() => fetchSoci()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Registro Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Libro dei Soci
          </CardTitle>
          <CardDescription>
            Elenco completo dei soci iscritti all'associazione
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Caricamento...</div>
          ) : soci.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessun socio trovato</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-bold">N.</TableHead>
                    <TableHead className="font-bold">Cognome e Nome</TableHead>
                    <TableHead className="font-bold">Codice Fiscale</TableHead>
                    <TableHead className="font-bold hidden md:table-cell">Data Nascita</TableHead>
                    <TableHead className="font-bold hidden lg:table-cell">Residenza</TableHead>
                    <TableHead className="font-bold">Tipo</TableHead>
                    <TableHead className="font-bold">Iscrizione</TableHead>
                    <TableHead className="font-bold">Stato</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {soci.map((socio, index) => (
                    <TableRow key={socio.persona_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <TableCell className="font-mono font-bold">{socio.numero_tessera || (index + 1)}</TableCell>
                      <TableCell className="font-medium">
                        {socio.nome_completo || `${socio.cognome || ''} ${socio.nome || ''}`.trim() || '-'}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{socio.codice_fiscale}</TableCell>
                      <TableCell className="hidden md:table-cell">{formatDateIT(socio.data_nascita) || '-'}</TableCell>
                      <TableCell className="hidden lg:table-cell">{socio.citta || '-'}</TableCell>
                      <TableCell className="capitalize">{socio.tipo_socio || 'ordinario'}</TableCell>
                      <TableCell>{formatDateIT(socio.data_iscrizione) || '-'}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatoBadge(socio.stato_socio)}`}>
                          {socio.stato_socio || 'attivo'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Print-friendly note */}
      <p className="text-sm text-gray-500 text-center">
        Per stampare il registro, esporta in CSV e apri con Excel/LibreOffice, oppure usa Ctrl+P per stampare questa pagina.
      </p>
    </div>
  );
}
